import dayjs, { Dayjs } from "dayjs";
import { AddonType, CampaignStatus } from "src/hooks/affiliate";

/**
 * A preset campaign template — drop-in values for the "สร้าง Affiliate Campaign"
 * form. The user picks "เทมเพลต", selects one of these, the form auto-fills, then
 * they tweak (code, dates, rates) and submit. Mirrors `CampaignFormValues` in
 * AffiliateCampaignCreateModal so it can be passed straight to `form.setFieldsValue`.
 *
 * How the "ยอดเสียเพื่อนตามจำนวนเพื่อน" tiers map to the data model:
 *   - The friend-count brackets are REFERRAL_COUNT addons. The daily batch
 *     (affiliate-campaign-daily-batch) matches the affiliate's downline count
 *     against each addon's min/max range and applies that bracket's
 *     commissionRate and limitPerUser (the บาท/วัน/คน cap).
 *   - A Tier 1 row at 100% is required: the batch commission formula is
 *     base_loss × (tier_rate/100) × (addon_rate/100), so tier 100% makes the
 *     bracket rate the effective % of the friend's loss.
 */
export interface CampaignTemplateAddon {
  addonType: AddonType;
  minValue?: number;
  maxValue?: number;
  /** Per-user/day commission cap (บาท/วัน/คน). */
  limitPerUser?: number;
  commissionRate: number;
}

export interface CampaignTemplateValues {
  campaignCode: string;
  campaignName: string;
  description?: string;
  dateRange: [Dayjs, Dayjs];
  status: CampaignStatus;
  isActive: boolean;
  tiers: { tierNumber: number; commissionRate: number }[];
  addons: CampaignTemplateAddon[];
  quests: never[];
}

export interface CampaignTemplate {
  id: string;
  name: string;
  description: string;
  /** Builds fresh form values (dates are dayjs instances, so build lazily). */
  build: () => CampaignTemplateValues;
}

export const CAMPAIGN_TEMPLATES: CampaignTemplate[] = [
  {
    id: "refer-friend-loss",
    name: "Campaign 1: แนะนำเพื่อน รับยอดเสียเพื่อน",
    description:
      "ลิงก์เริ่มต้น pigslot.com/ref/[รหัสตัวแทน] · รับยอดเสียเพื่อนตามจำนวนเพื่อน " +
      "(1-4 คน 10%, 5-9 คน 15%, 10+ คน 20%)",
    build: () => ({
      campaignCode: "REFER_FRIEND_2026",
      campaignName: "แนะนำเพื่อน รับยอดเสียเพื่อน",
      description:
        "ลิงก์เริ่มต้น: pigslot.com/ref/[รหัสตัวแทน]\n" +
        "รับยอดเสียเพื่อนตามจำนวนเพื่อน:\n" +
        "- เพื่อน 1-4 คน → รับยอดเสียเพื่อน 10% (สูงสุด 500 บาท/วัน/คน)\n" +
        "- เพื่อน 5-9 คน → รับยอดเสียเพื่อน 15% (สูงสุด 1,000 บาท/วัน/คน)\n" +
        "- เพื่อน 10 คนขึ้นไป → รับยอดเสียเพื่อน 20% (สูงสุด 1,500 บาท/วัน/คน)",
      dateRange: [
        dayjs("2026-07-01").startOf("day"),
        dayjs("2026-12-31").endOf("day"),
      ],
      status: CampaignStatus.ACTIVE,
      isActive: true,
      // Tier 1 = 100% so the REFERRAL_COUNT addon rate is the effective % of loss.
      tiers: [{ tierNumber: 1, commissionRate: 100 }],
      addons: [
        { addonType: AddonType.REFERRAL_COUNT, minValue: 1, maxValue: 4, commissionRate: 10, limitPerUser: 500 },
        { addonType: AddonType.REFERRAL_COUNT, minValue: 5, maxValue: 9, commissionRate: 15, limitPerUser: 1000 },
        { addonType: AddonType.REFERRAL_COUNT, minValue: 10, commissionRate: 20, limitPerUser: 1500 },
      ],
      quests: [],
    }),
  },
];
